import { create } from 'zustand';
import {
  ListPaymentTypes,
  CreatePaymentType,
  GetPaymentType,
  UpdatePaymentType,
  DeletePaymentType,
  PaymentType,
  CreatePaymentTypeI,
  UpdatePaymentTypeI
} from '../api/api';
import toast from 'react-hot-toast';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetPaymentTypesResponse {
  data: {
    paymentTypes: PaymentType[];
    pagination?: PaginationInfo;
  };
}

interface PaymentTypeResponse {
  data: {
    paymentType: PaymentType;
  };
  status: boolean;
  message: string;
}

interface DeletePaymentTypeResponse {
  status: boolean;
  message: string;
}

interface PaymentTypeStore {
  paymentTypes: PaymentType[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getPaymentTypes: (search?: string, page?: number, limit?: number, isActive?: boolean) => Promise<void>;
  createPaymentType: (paymentType: CreatePaymentTypeI) => Promise<void>;
  getPaymentTypeById: (id: string) => Promise<PaymentType | null>;
  updatePaymentType: (id: string, paymentType: UpdatePaymentTypeI) => Promise<void>;
  deletePaymentType: (id: string) => Promise<void>;
}

export const usePaymentTypeStore = create<PaymentTypeStore>((set, get) => ({
  paymentTypes: [] as PaymentType[],
  loading: false,
  error: null,
  pagination: null,

  getPaymentTypes: async (search?: string, page?: number, limit?: number, isActive?: boolean) => {
    set({ loading: true, error: null });
    const { data, error } = await ListPaymentTypes<GetPaymentTypesResponse>(search, page, limit, isActive);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      paymentTypes: data.data.paymentTypes || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createPaymentType: async (paymentType: CreatePaymentTypeI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreatePaymentType<PaymentTypeResponse>(paymentType);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create payment type' });
      return;
    }

    const paymentTypes = get().paymentTypes;
    set({ paymentTypes: [data.data.paymentType, ...paymentTypes], loading: false });
    toast.success('Payment type created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentTypes(undefined, currentPagination?.page, currentPagination?.limit);
  },

  getPaymentTypeById: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetPaymentType<PaymentTypeResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get payment type' });
      return null;
    }

    set({ loading: false });
    return data.data.paymentType;
  },

  updatePaymentType: async (id: string, paymentType: UpdatePaymentTypeI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdatePaymentType<PaymentTypeResponse>(id, paymentType);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update payment type' });
      return;
    }

    const paymentTypes = get().paymentTypes;
    const updatedPaymentTypes = paymentTypes.map((pt) => (pt._id === id ? { ...pt, ...data.data.paymentType } : pt));
    set({ paymentTypes: updatedPaymentTypes, loading: false });
    toast.success('Payment type updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentTypes(undefined, currentPagination?.page, currentPagination?.limit);
  },

  deletePaymentType: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeletePaymentType<DeletePaymentTypeResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete payment type' });
      return;
    }

    const paymentTypes = get().paymentTypes.filter((pt) => pt._id !== id);
    set({ paymentTypes, loading: false });
    toast.success('Payment type deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPaymentTypes(undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { PaymentType, CreatePaymentTypeI, UpdatePaymentTypeI };
